import { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, MapPin, Globe, Check, Loader2, Crosshair, X, Users } from 'lucide-react';
import { api, compact } from '../lib/api';
import { COUNTRIES, CONTINENTS, flagOf, guessCountry, getTimezone } from '../lib/countries';

export default function CountryCityPicker({ value, onChange }) {
  const [step, setStep] = useState(value?.countryCode ? 'city' : 'country');
  const [countrySearch, setCountrySearch] = useState('');
  const [citySearch, setCitySearch] = useState('');
  const [continent, setContinent] = useState('');
  const [geoLoading, setGeoLoading] = useState(false);
  const cityInputRef = useRef(null);

  useEffect(() => {
    if (value?.countryCode) return;
    const g = guessCountry();
    if (g.code) {
      const c = COUNTRIES.find(x => x.code === g.code);
      if (c) {
        onChange({ ...value, countryCode: c.code, countryName: c.name, timezone: g.timezone });
        setStep('city');
      }
    }
  }, []);

  const { data: apiCountries } = useQuery({
    queryKey: ['apiCountries'],
    queryFn: () => api.get('/groups/countries').then(r => r.data),
  });
  const countryMap = useMemo(
    () => Object.fromEntries((apiCountries || []).map(c => [c.country_code, c])),
    [apiCountries]);

  const { data: cities, isLoading: citiesLoading } = useQuery({
    queryKey: ['cities', value?.countryCode, citySearch],
    queryFn: () => api.get('/groups/cities', {
      params: { country: value.countryCode, q: citySearch || undefined, limit: 60 },
    }).then(r => r.data),
    enabled: !!value?.countryCode && step === 'city',
  });

  const filteredCountries = useMemo(() => {
    const t = countrySearch.toLowerCase().trim();
    return COUNTRIES
      .filter(c => (!continent || c.continent === continent) &&
        (!t || c.name.toLowerCase().includes(t) || c.code.toLowerCase() === t))
      .sort((a, b) => {
        const pa = countryMap[a.code]?.population || 0;
        const pb = countryMap[b.code]?.population || 0;
        return pb - pa || a.name.localeCompare(b.name);
      });
  }, [countrySearch, continent, countryMap]);

  function pickCountry(c) {
    onChange({
      ...value,
      countryCode: c.code,
      countryName: c.name,
      countryGroupId: countryMap[c.code]?.id || null,
      cityGroupId: null, cityName: null,
      timezone: value?.timezone || getTimezone(),
    });
    setStep('city');
    setCitySearch('');
    setTimeout(() => cityInputRef.current?.focus(), 250);
  }

  function pickCity(city) {
    onChange({
      ...value,
      cityGroupId: city.id,
      cityName: city.name,
      cityHandle: city.handle,
      timezone: city.timezone || value?.timezone || getTimezone(),
    });
  }

  async function useMyLocation() {
    if (!navigator.geolocation) return;
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { data } = await api.get('/groups/nearby', {
            params: { lat: pos.coords.latitude, lng: pos.coords.longitude },
          });
          if (data?.length) {
            const n = data[0];
            const c = COUNTRIES.find(x => x.code === n.country_code);
            onChange({
              ...value,
              countryCode: n.country_code, countryName: c?.name,
              countryGroupId: countryMap[n.country_code]?.id || null,
              cityGroupId: n.id, cityName: n.name, cityHandle: n.handle,
              timezone: n.timezone || getTimezone(),
            });
            setStep('city');
          }
        } finally { setGeoLoading(false); }
      },
      () => setGeoLoading(false),
      { timeout: 8000 }
    );
  }

  /* ═══════ COUNTRY ═══════ */
  if (step === 'country') {
    return (
      <div className="space-y-2.5">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
            <input autoFocus className="input pl-9 !py-2.5" placeholder="Search your country…"
              value={countrySearch} onChange={e => setCountrySearch(e.target.value)} />
          </div>
          <button type="button" onClick={useMyLocation} disabled={geoLoading}
            title="Use my location" className="btn-ghost !px-3 shrink-0">
            {geoLoading ? <Loader2 size={15} className="animate-spin" /> : <Crosshair size={15} />}
          </button>
        </div>

        <div className="flex gap-1.5 overflow-x-auto no-scrollbar">
          <button type="button" onClick={() => setContinent('')}
            className={`px-2.5 py-1.5 rounded-lg text-[11px] font-bold whitespace-nowrap transition
              ${!continent ? 'bg-ink-900 text-white' : 'bg-white border border-ink-200 text-ink-500'}`}>
            🌍 All
          </button>
          {Object.values(CONTINENTS).map(c => (
            <button key={c.key} type="button" onClick={() => setContinent(c.key)}
              className={`px-2.5 py-1.5 rounded-lg text-[11px] font-bold whitespace-nowrap transition
                ${continent === c.key ? 'bg-ink-900 text-white' : 'bg-white border border-ink-200 text-ink-500'}`}>
              {c.emoji} {c.label}
            </button>
          ))}
        </div>

        <div className="max-h-[280px] overflow-y-auto space-y-1 -mx-0.5 px-0.5">
          {filteredCountries.map(c => {
            const g = countryMap[c.code];
            return (
              <button key={c.code} type="button" onClick={() => pickCountry(c)}
                className="w-full flex items-center gap-2.5 p-2.5 rounded-xl bg-white border
                  border-ink-100 hover:border-brand-300 hover:bg-brand-50/40 transition text-left">
                <span className="text-xl shrink-0">{flagOf(c.code)}</span>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm truncate">{c.name}</p>
                  <p className="text-[10px] text-ink-400 font-semibold truncate">
                    {c.continent}
                    {g?.cities > 0 && ` · ${g.cities} cities`}
                    {g?.population > 0 && ` · ${compact(g.population)} people`}
                  </p>
                </div>
                <span className="text-[9px] font-black text-ink-300 shrink-0">{c.code}</span>
              </button>
            );
          })}
          {!filteredCountries.length && (
            <p className="text-center text-sm text-ink-400 py-6">No countries found</p>
          )}
        </div>
      </div>
    );
  }

  /* ═══════ CITY ═══════ */
  const selectedCountry = COUNTRIES.find(c => c.code === value?.countryCode);

  return (
    <div className="space-y-2.5">
      <button type="button" onClick={() => setStep('country')}
        className="w-full flex items-center gap-2.5 p-2.5 rounded-xl bg-brand-50
          border-2 border-brand-300 text-left">
        <span className="text-xl shrink-0">{flagOf(value.countryCode)}</span>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-sm truncate">{selectedCountry?.name}</p>
          <p className="text-[10px] text-brand-600 font-bold">Tap to change</p>
        </div>
        <X size={14} className="text-ink-400 shrink-0" />
      </button>

      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
        <input ref={cityInputRef} className="input pl-9 !py-2.5" placeholder="Search your city…"
          value={citySearch} onChange={e => setCitySearch(e.target.value)} />
        {citiesLoading && (
          <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-ink-400" />
        )}
      </div>

      <div className="max-h-[260px] overflow-y-auto space-y-1 -mx-0.5 px-0.5">
        {cities?.map(city => {
          const active = value?.cityGroupId === city.id;
          return (
            <motion.button key={city.id} type="button" onClick={() => pickCity(city)}
              whileTap={{ scale: 0.98 }}
              className={`w-full flex items-center gap-2.5 p-2.5 rounded-xl border-2 transition text-left
                ${active ? 'border-brand-500 bg-brand-50' : 'border-ink-100 bg-white hover:border-ink-300'}`}>
              <div className={`w-8 h-8 rounded-lg grid place-items-center shrink-0 text-sm
                ${active ? 'bg-brand-500 text-white' : 'bg-ink-100 text-ink-400'}`}>
                {city.is_capital ? '⭐' : <MapPin size={14} />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <p className="font-bold text-sm truncate">{city.name}</p>
                  {city.is_capital ? (
                    <span className="chip-brand !text-[7px] !px-1 shrink-0">CAPITAL</span>
                  ) : null}
                </div>
                <p className="text-[10px] text-ink-400 font-semibold truncate">
                  {city.handle}
                  {city.region && ` · ${city.region}`}
                  {city.population > 0 && ` · ${compact(city.population)} people`}
                  {city.shops > 0 && ` · ${city.shops} shops`}
                </p>
              </div>
              {active && <Check size={15} className="text-brand-600 shrink-0" strokeWidth={3} />}
            </motion.button>
          );
        })}

        {!citiesLoading && cities?.length === 0 && (
          <div className="text-center py-6">
            <Globe size={24} className="mx-auto text-ink-300 mb-1.5" />
            <p className="text-sm font-bold text-ink-500">No cities found</p>
            <p className="text-xs text-ink-400 mt-0.5">Try a different search.</p>
          </div>
        )}
      </div>

      <AnimatePresence>
        {value?.cityGroupId && (
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
            className="p-2.5 rounded-xl bg-mint-400/10 border border-mint-400/30">
            <p className="text-[11px] font-bold text-mint-700">
              ✓ Joining <b>{value.cityHandle}</b> and{' '}
              <b>@{selectedCountry?.name?.toLowerCase().replace(/\s+/g, '-')}</b>
            </p>
            <p className="text-[10px] text-mint-600 font-semibold mt-0.5">
              You can explore any city worldwide anytime.
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}